<template>
  <div class="__dt-input" :class="{ '__dt-input-inline' : inline}">
    <div :class="[`--${size}`]" class="__label">
      <slot name="label" v-bind:item="label">
        {{ label }}
      </slot>
    </div>

    <input
      class="__input"
      ref="dtInput"
      :id="id"
      :name="name"
      :type="type"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :class="[{inputError : error}, `--${size}`]"
      :value="value"
      :style="setWidth"
      @keyup.enter="saveValue($event)"
      @focus="getValue(value)"
      @input="sendValue($event)"
      @blur="resetValue()"
    >

    <!-- Fake Container to Adjust Inline Input Width -->
    <span v-if="value" class="fake-input" :class="[`--${size}`]" ref="fakeInput">{{value}}</span>
    <span v-else class="fake-input" :class="[`--${size}`]" ref="fakeInput">{{placeholder}}</span>
    
    <!-- Save & Discard Icons For Inline Input -->
    <div v-show="inlineBtn && inline" class="inline-buttons">
      <span :class="[`--${size}`]" class="fal fa-check inline-save" @mousedown="save"></span>
      <span :class="[`--${size}`]" class="fal fa-times inline-discard" @mousedown="discard"></span>
    </div>

    <small v-show="error" :class="[`--${size}`]" class="__error">{{ error }}</small>
  </div>
</template>
 
<script>
export default {
  // Loads external data
  model: {
    prop: "value",
    event: "update"
  },

  props: {
    id: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    }, 
    label: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    }, 
    placeholder: {
      type: String,
      default: null,
    }, 
    value: {
      type: [String,Number],
      default: null,
    }, 
    type: {
      type: String,
      default: 'text',
      validator: function (value) {
        return ['text', 'number', 'email', 'password', 'search', 'url', 'tel', 'date', 'time', 'range', 'color'].includes(value)
      }
    }, 
    size: {
      type: String,
      default: 'md',
      validator: function (value) {
        return ['sm', 'md', 'lg'].includes(value)
      }
    },
    required: {
      type: Boolean,
      default: false,
    }, 
    disabled: {
      type: Boolean,
      default: false,
    }, 
    error: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false
    },
    debounce: {
      type: [String, Number],
      default: 0
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    trim: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false,
    }, 
  },

  data () {
    return {
      timeOut: null,
      initialValue: null,
      clicks: 0,
      inlineBtn: false,
      width: 'auto'
    }
  },

  computed: {
    setWidth() {
      return {
        "width": `${this.width}`
      }
    }
  },

  mounted() {
    this.focusInput()

    if(this.inline){
      this.modifyWidth()
    } 
  },

  methods: {
    focusInput() {
      if (this.autofocus) {
        this.$refs.dtInput.focus()
      }  
    },

    sendValue(event) {
      if (this.debounce === 0) {
        if (this.trim) {
          this.$emit('update', event.target.value.trim())
        } else {
          this.$emit('update', event.target.value)
        }
      } else {
        clearTimeout(this.timeOut)
        this.timeOut = setTimeout(() => {
          if (this.trim) {
            this.$emit('update', event.target.value.trim())
          } else {
            this.$emit('update', event.target.value)
          }
        }, this.debounce)
      }
    },

    saveValue(event) {
      this.sendValue(event)
      event.target.blur()
    },
    
    getValue(value){
      this.clicks++
      if(this.inline){
        this.width = "auto"
      }       
      if(this.clicks == 1){
        this.initialValue = value
        this.inlineBtn = true
      }
    },

    resetValue(){
      if(this.inline){
        this.modifyWidth()
      }
      this.clicks = 0
      this.inlineBtn = false
    },

    save(){
      this.resetValue()
      this.$emit('update', this.value)
    },
     
    discard(){
      this.resetValue()
      this.$emit('update', this.initialValue)
    },

    modifyWidth(){
      switch(this.type) {
        case "date":
          this.width = (this.$refs.fakeInput.clientWidth+5)+"px"
          break;
        case "time":
          this.width = (this.$refs.fakeInput.clientWidth+35)+"px"
          break;
        default:
          this.width = (this.$refs.fakeInput.clientWidth)+"px"
      }
    }
  }

}
</script>
                
<style lang="scss" scoped>
  // Default Input 
  .__dt-input {
    display: flex;
    flex-direction: column;

    // Input Field
    .__input {
      font-weight: 500;
      color: var(--gray-800);
      border: 1px solid var(--gray-300);
      border-radius: 3px;
      outline: none;
      padding: .375rem .75rem;

      &.--sm {
        height: 31px;
        font-size: 0.78rem;
      }
      &.--md {
        height: 40px;
        font-size: 14px;
      }
      &.--lg {
        height: 48px;
        font-size: 1.2rem;
      }
    }

    .__input:focus {
      border: 1px solid var(--primary);
      transition: border-color 0.5s ease;  
    }

    .__input::placeholder {
      color: var(--gray-400);
      font-weight: 300;
    }

    // Fake Container to Adjust Inline Input Width
    .fake-input{
      font-weight: 500;
      padding-left: .32rem;
      padding-right: .28rem;
      width: fit-content!important;
      height: 0px;
      visibility: hidden!important;
      &.--sm {
        font-size: 0.78rem;
      }
      &.--md {
        font-size: 14px;
      }
      &.--lg {
        font-size: 1.2rem;
      }
    }

    // Label
    .__label {
      font-size: 14px;
      color: var(--gray-700);
      margin-bottom: 5px;

      &.--sm {
        font-size: 0.78rem;
      }
      &.--md {
        font-size: 14px;
      }
      &.--lg {
        font-size: 1.2rem;
      }
    }

    // Error
    .inputError{
      border: 1px solid var(--danger);
    }

    .inputError:focus{
      border: 1px solid var(--danger);
    }

    .__error {
      margin-top: 3px;
      color: var(--danger);

      &.--sm {
        font-size: 0.78rem;
      }
      &.--md {
        font-size: 13px;
      }
      &.--lg {
        font-size: 1.2rem;
      }
    }
  }

  // Inline Input
  .__dt-input-inline{
    position: relative;

    // Inline Field
    .__input {
      background: none;
      border: none;
      padding-left: .32rem;
      padding-right: .28rem;
      transition: background-color 0.2s ease;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .__input:hover {
      cursor: pointer;
      background-color: var(--gray-200);
      transition: background-color 0.2s ease;
    }

    .__input:focus {
      padding-left: .25rem;
      cursor: text;
      background-color: var(--white);
      border: 1px solid var(--primary);  
      &.--sm {
        padding-right: 3.8rem;
      }
      &.--md {
        padding-right: 4.6rem;
      }
      &.--lg {
        padding-right: 5.4rem;
      }    
    }

    // Save and Discard Icons
    .inline-save, .inline-discard {
      position: absolute;
      color: var(--gray-600);
      background-color: var(--gray-100);
      border-radius: 2px;      
      cursor: pointer;  
      display: flex;
      align-items: center;
      justify-content: center;     
      
      &.--sm {
        top: 29px;
        width: 20px;
        height: 20px;
        font-size: 10px;
      }

      &.--md {
        top: 33.5px;
        width: 25px;
        height: 25px;
        font-size: 13px;
      }

      &.--lg {
        top: 43px;
        width: 31px;
        height: 31px;
        font-size: 1.2rem;
      }
    }

    .inline-save:hover{
      background-color: var(--primary-faded);
      color: var(--primary);
      transition: background-color 0.2s ease;
    }

    .inline-discard:hover{
      background-color: var(--gray-200);
      color: var(--danger);
      transition: background-color 0.2s ease;
    }

    // Save and Discard Positions
    .inline-save{       
      &.--sm {
        right: 33px;
      }

      &.--md {
        right: 40px;
      }
      
      &.--lg {
        right: 45px;
      }
    }

    .inline-discard{
      right: 8px;
    }
  }
</style>